import { PaymentTermMapper } from '../mappers/payment-term.mapper';
import { inject, Injectable } from '@angular/core';
import { IPaginatedItemsModel } from '@shared/sync/paginated-items.model';
import { PaymentTerm } from '../models/payment-term.model';
import { SortBy } from '@shared/sync/sort-by.model';
import { FilterBy } from '@shared/sync/filter-by.model';
import { HsBlazorService } from '@hsetu/core-services';

@Injectable({
  providedIn: 'root',
})
export class PaymentTermService {
  private readonly _serviceKey = 'kfmPaymentTerm';
  private readonly _blazorService = inject(HsBlazorService);
  private readonly _mapper = new PaymentTermMapper();

  public async getAll(
    pageSize = 10,
    pageIndex = 0,
    sortBy: SortBy | null = null,
    filterBy: FilterBy | null = null,
  ): Promise<IPaginatedItemsModel<PaymentTerm>> {
    const termResult = await this._blazorService.invokeService(
      this._serviceKey,
      'Get',
      {
        pageIndex: pageIndex,
        pageSize: pageSize,
        sortBy: sortBy,
        filterBy: filterBy,
      },
    );

    if (termResult) {
      const result = (
        termResult as Record<string, IPaginatedItemsModel<PaymentTerm>>
      )['result'];

      return result;
    }

    return { count: 0, data: [], pageSize: 0, pageIndex: 0 };
  }

  public async get(id: string): Promise<PaymentTerm | undefined> {
    const paymentTermResult = await this._blazorService.invokeService(
      this._serviceKey,
      'GetById',
      {
        id: id,
      },
    );

    const result = (paymentTermResult as Record<string, unknown>)[
      'paymentTerm'
    ];

    return result ? (result as PaymentTerm) : undefined;
  }

  public async create(value: PaymentTerm): Promise<PaymentTerm | undefined> {
    const request = this._mapper.mapPaymentTermRequest(value);
    const result = await this._blazorService.invokeService(
      this._serviceKey,
      'Put',
      request,
    );

    if (result) {
      const id = (result as Record<string, unknown>)['id'];
      value.uId = id as string;
      return value;
    }
  }

  public async update(value: PaymentTerm): Promise<boolean> {
    const request = this._mapper.mapPaymentTermRequest(value);
    const result = await this._blazorService.invokeService(
      this._serviceKey,
      'Put',
      request,
    );

    return !!result;
  }

  public async delete(id: string): Promise<boolean> {
    const result = await this._blazorService.invokeService(
      this._serviceKey,
      'Delete',
      { id: id },
    );

    const deleted = (result as Record<string, unknown>)['deleted'];
    return !!deleted;
  }

  public async alreadyInUse(paymentTerm: PaymentTerm): Promise<boolean> {
    const result = await this._blazorService.invokeService(
      this._serviceKey,
      'AlreadyInUse',
      {
        id: paymentTerm.uId,
      },
    );

    const alreadyInUse = (result as Record<string, unknown>)['alreadyInUse'];
    return !!alreadyInUse;
  }
}
